import { useEffect, useState } from "react";
import { Download, Share, Plus, X, Smartphone, Monitor, Apple } from "lucide-react";
import { useTranslation } from "react-i18next";

const LOGO = "https://customer-assets." + atob("ZW1lcmdlbnRhZ2VudC5jb20=") + "/job_activity-squad-2/artifacts/4cw1jeaq_1a81f51e-4e78-47f6-81c3-cfddeb7a76dc.png";

const isIOS = () => {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || navigator.vendor || "";
  return /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
};

const isStandalone = () => {
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
};

export default function InstallPrompt() {
  const { t } = useTranslation();
  const [deferred, setDeferred] = useState(null);
  const [showCard, setShowCard] = useState(false);
  const [showSheet, setShowSheet] = useState(false);
  const [installed, setInstalled] = useState(false);

  useEffect(() => {
    if (isStandalone()) { setInstalled(true); return; }
    if (localStorage.getItem("tanta-install-dismissed") === "1") return;

    const onBefore = (e) => {
      e.preventDefault();
      setDeferred(e);
      setShowCard(true);
    };
    const onInstalled = () => { setInstalled(true); setShowCard(false); setShowSheet(false); };
    window.addEventListener("beforeinstallprompt", onBefore);
    window.addEventListener("appinstalled", onInstalled);

    // iOS Safari has no beforeinstallprompt — show our own card after a brief delay
    if (isIOS() && !isStandalone()) {
      const t = setTimeout(() => setShowCard(true), 2500);
      return () => {
        clearTimeout(t);
        window.removeEventListener("beforeinstallprompt", onBefore);
        window.removeEventListener("appinstalled", onInstalled);
      };
    }
    return () => {
      window.removeEventListener("beforeinstallprompt", onBefore);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  if (installed) return null;

  const dismiss = () => {
    setShowCard(false);
    localStorage.setItem("tanta-install-dismissed", "1");
  };

  const triggerInstall = async () => {
    if (deferred) {
      deferred.prompt();
      const choice = await deferred.userChoice.catch(() => null);
      setDeferred(null);
      setShowCard(false);
      if (choice?.outcome === "accepted") setInstalled(true);
    } else {
      // iOS — open instructions sheet
      setShowSheet(true);
    }
  };

  return (
    <>
      {showCard && (
        <div
          className="fixed bottom-24 sm:bottom-6 left-3 right-3 sm:left-auto sm:right-6 sm:max-w-sm z-30 glass-strong rounded-2xl shadow-soft-lg p-4 animate-pop flex items-center gap-3"
          data-testid="install-card"
        >
          <img src={LOGO} alt="" className="w-10 h-10 object-contain shrink-0"/>
          <div className="flex-1 min-w-0">
            <div className="font-display font-extrabold text-sm tracking-tight">{t("install.installTanta")}</div>
            <div className="text-xs text-[#4A4F58]">{t("install.oneTap")}</div>
          </div>
          <button onClick={triggerInstall} className="btn-nb btn-nb-primary !py-2 !px-3 !text-xs" data-testid="install-card-btn">
            <Download className="w-3.5 h-3.5" strokeWidth={2.5}/> {t("install.install")}
          </button>
          <button onClick={dismiss} className="btn-nb !p-2" aria-label="Dismiss" data-testid="install-card-dismiss">
            <X className="w-3.5 h-3.5" strokeWidth={2.5}/>
          </button>
        </div>
      )}

      {showSheet && (
        <div className="fixed inset-0 bg-[#0F1115]/40 backdrop-blur-sm z-50 flex items-end sm:items-center justify-center p-4" onClick={() => setShowSheet(false)}>
          <div className="card-nb p-6 max-w-md w-full bg-white animate-pop" onClick={(e) => e.stopPropagation()} data-testid="install-sheet">
            <div className="flex items-center gap-3 mb-4">
              <img src={LOGO} alt="TANTA" className="w-12 h-12 object-contain"/>
              <div>
                <div className="font-display font-extrabold text-lg tracking-tight">{t("install.installTanta")}</div>
                <div className="text-xs text-[#8A8F98]">{t("install.addHome")}</div>
              </div>
              <button onClick={() => setShowSheet(false)} className="btn-nb !p-2 ml-auto" aria-label="Close"><X className="w-4 h-4" strokeWidth={2.5}/></button>
            </div>

            {isIOS() ? (
              <ol className="space-y-3 text-sm text-[#0F1115]">
                <li className="flex items-start gap-3 p-3 rounded-2xl bg-[#DEF1F5] border border-[#0F1115]/8">
                  <span className="font-display font-extrabold text-lg leading-none">1</span>
                  <span>Tap the <b className="inline-flex items-center gap-1"><Share className="w-4 h-4 inline" strokeWidth={2.5}/> Share</b> button at the bottom of Safari.</span>
                </li>
                <li className="flex items-start gap-3 p-3 rounded-2xl bg-[#DDF5EA] border border-[#0F1115]/8">
                  <span className="font-display font-extrabold text-lg leading-none">2</span>
                  <span>Scroll and tap <b className="inline-flex items-center gap-1"><Plus className="w-4 h-4 inline" strokeWidth={2.5}/> Add to Home Screen</b>.</span>
                </li>
                <li className="flex items-start gap-3 p-3 rounded-2xl bg-[#FFF1D6] border border-[#0F1115]/8">
                  <span className="font-display font-extrabold text-lg leading-none">3</span>
                  <span>Tap <b>Add</b>. TANTA lands on your home screen, no app store needed.</span>
                </li>
              </ol>
            ) : (
              <div className="space-y-3 text-sm">
                <div className="flex items-start gap-3 p-3 rounded-2xl bg-[#DDF5EA] border border-[#0F1115]/8">
                  <Smartphone className="w-5 h-5 mt-0.5 shrink-0" strokeWidth={2.25}/>
                  <span><b>Android:</b> Tap the menu (⋮) in Chrome → <b>Install app</b>.</span>
                </div>
                <div className="flex items-start gap-3 p-3 rounded-2xl bg-[#DEF1F5] border border-[#0F1115]/8">
                  <Monitor className="w-5 h-5 mt-0.5 shrink-0" strokeWidth={2.25}/>
                  <span><b>Windows / Mac / Linux:</b> Click the install icon in the address bar (Chrome/Edge), or use the menu → <b>Install TANTA</b>.</span>
                </div>
                <div className="flex items-start gap-3 p-3 rounded-2xl bg-[#FFE6E0] border border-[#0F1115]/8">
                  <Apple className="w-5 h-5 mt-0.5 shrink-0" strokeWidth={2.25}/>
                  <span><b>iPhone / iPad:</b> Open in Safari, tap Share → Add to Home Screen.</span>
                </div>
              </div>
            )}

            <button onClick={() => setShowSheet(false)} className="btn-nb btn-nb-primary w-full mt-5" data-testid="install-sheet-got-it">{t("install.gotIt")}</button>
          </div>
        </div>
      )}
    </>
  );
}

export function InstallButton({ className = "" }) {
  const { t } = useTranslation();
  const [available, setAvailable] = useState(false);
  const [deferred, setDeferred] = useState(null);
  const [showSheet, setShowSheet] = useState(false);

  useEffect(() => {
    if (isStandalone()) return;
    const onBefore = (e) => { e.preventDefault(); setDeferred(e); setAvailable(true); };
    window.addEventListener("beforeinstallprompt", onBefore);
    if (isIOS()) setAvailable(true);
    return () => window.removeEventListener("beforeinstallprompt", onBefore);
  }, []);

  if (!available) return null;

  const onClick = async () => {
    if (deferred) {
      deferred.prompt();
      await deferred.userChoice.catch(() => null);
      setDeferred(null);
      setAvailable(false);
    } else {
      setShowSheet(true);
    }
  };

  return (
    <>
      <button onClick={onClick} className={`btn-nb btn-nb-primary ${className}`} data-testid="install-button">
        <Download className="w-4 h-4" strokeWidth={2.5}/> {t("install.installApp")}
      </button>
      {showSheet && (
        <div className="fixed inset-0 bg-[#0F1115]/40 backdrop-blur-sm z-50 flex items-end sm:items-center justify-center p-4" onClick={() => setShowSheet(false)}>
          <div className="card-nb p-6 max-w-md w-full bg-white animate-pop" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-3 mb-4">
              <img src={LOGO} alt="TANTA" className="w-12 h-12 object-contain"/>
              <div className="font-display font-extrabold text-lg tracking-tight">{t("install.installTanta")}</div>
            </div>
            <ol className="space-y-3 text-sm">
              <li className="flex items-start gap-3 p-3 rounded-2xl bg-[#DEF1F5] border border-[#0F1115]/8">
                <span className="font-display font-extrabold">1.</span>
                <span>Tap <b><Share className="w-4 h-4 inline" strokeWidth={2.5}/> Share</b> in Safari.</span>
              </li>
              <li className="flex items-start gap-3 p-3 rounded-2xl bg-[#DDF5EA] border border-[#0F1115]/8">
                <span className="font-display font-extrabold">2.</span>
                <span>Tap <b><Plus className="w-4 h-4 inline" strokeWidth={2.5}/> Add to Home Screen</b>.</span>
              </li>
              <li className="flex items-start gap-3 p-3 rounded-2xl bg-[#FFF1D6] border border-[#0F1115]/8">
                <span className="font-display font-extrabold">3.</span>
                <span>Tap <b>Add</b>. Done.</span>
              </li>
            </ol>
            <button onClick={() => setShowSheet(false)} className="btn-nb btn-nb-primary w-full mt-5">{t("install.gotIt")}</button>
          </div>
        </div>
      )}
    </>
  );
}
